// File: app/src/apis/llm/reasoning.js
//
// OpenRouter's unified reasoning controls, kept pure so they can be tested.
//
// OpenRouter normalises every upstream vendor's reasoning knob onto one
// request field — `reasoning: { effort }` — and returns the reasoning trace on
// `delta.reasoning` while streaming. Neither is sent unless we ask, which is
// why a Gemini model routed through OpenRouter showed no thinking output while
// the same model on the Google provider did.
//
// Imports only the provider config, never `providers.js` or `geminiApi`: the
// Gemini SDK is ESM-only and Jest cannot transform it, so anything importing
// it is untestable.

import { LLM_PROVIDERS } from "../../config/llmProviders";

// OpenRouter's shortlist mixes reasoning models with plain ones. The tag on
// the catalogue entry is what decides — sending `reasoning` to a model that
// has none buys nothing and reserves output headroom it will never use.
const REASONING_TAGS = ["reasoning", "thinking"];

export const supportsOpenRouterReasoning = (model) => {
  const tags = LLM_PROVIDERS.openrouter?.specialFeatures?.[model];
  if (!Array.isArray(tags)) return false;
  return tags.some((tag) => REASONING_TAGS.includes(tag));
};

// The app stores one thinking level (`geminiThinkingLevel`), expressed in
// Gemini's vocabulary. Map it onto OpenRouter's effort scale. MINIMAL has no
// counterpart there, so it lands on the lowest effort OpenRouter accepts.
const EFFORT_BY_LEVEL = {
  MINIMAL: "low",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
};

export const toReasoningEffort = (level) => {
  if (level === "OFF") return null;
  return EFFORT_BY_LEVEL[String(level || "").toUpperCase()] || "medium";
};

// The `reasoning` request field, or null when this call should not ask for a
// trace at all.
export const buildOpenRouterReasoning = (model, level) => {
  if (!supportsOpenRouterReasoning(model)) return null;

  const effort = toReasoningEffort(level);
  if (!effort) return null;

  // `exclude: false` is the default, but it is the whole point of the request
  // here — state it so a future default flip cannot silently blank the panel.
  return { effort, exclude: false };
};

// Pull the reasoning text out of one streamed delta. Older responses carry a
// flat `reasoning` string; newer ones carry structured `reasoning_details`
// blocks, of which only the text-bearing ones are renderable.
export const readReasoningDelta = (delta) => {
  if (!delta || typeof delta !== "object") return "";
  if (typeof delta.reasoning === "string") return delta.reasoning;

  if (Array.isArray(delta.reasoning_details)) {
    return delta.reasoning_details
      .map((detail) => (typeof detail?.text === "string" ? detail.text : ""))
      .join("");
  }

  return "";
};
